import React, {
	ChangeEvent,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { Col, RadioChangeEvent, Row, message } from 'antd';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import * as Yup from 'yup';

// UTILS
import getValidationsErrors from 'utils/getValidationsErrors';
import hasError from 'utils/getFormErrors';
import {
	completaZeroEsquerda,
	limparMascara,
	patternFormat,
	removeAcentoCedilha,
	validaCpf,
	verifyDateIsLowerCurrentDate,
	validacaoData,
	convertDate,
	formatDate,
	calcularIdade,
} from 'utils/genericFunctions';
import { nomeIirgdSemNumeros } from 'utils/stringFunctions';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
	addNotifications,
	clearNotifications,
	showInside,
} from 'store/modules/app/notifications/actions';
import {
	consultarCidadaoAutistaRequest,
	consultarCidadaoAutistaClear,
} from 'store/modules/api/iirgd/cidadaoAutista/consultarCidadaoAutista/actions';
import {
	cadastrarCidadaoAutistaClear,
	cadastrarCidadaoAutistaRequest,
} from 'store/modules/api/iirgd/cidadaoAutista/cadastrarCidadaoAutista/actions';
import {
	atualizarCidadaoAutistaClear,
	atualizarCidadaoAutistaRequest,
} from 'store/modules/api/iirgd/cidadaoAutista/atualizarCidadaoAutista/actions';
import {
	consultarCpfCinRequest,
	consultarCpfCinClear,
} from 'store/modules/api/iirgd/integrador/consultarCin/actions';
import {
	consultarNaturalidadesClear,
	consultarNaturalidadesRequest,
} from 'store/modules/api/sgu/administrar/naturalidades/consultarNaturalidades/actions';
import { Login } from 'store/modules/api/sgu/loginUnico/types';
import {
	ConsultarCidadaoAutistaData,
	ConsultarCidadaoAutistaRequest,
} from 'store/modules/api/iirgd/cidadaoAutista/consultarCidadaoAutista/types';
import { CadastrarCidadaoAutistaRequest } from 'store/modules/api/iirgd/cidadaoAutista/cadastrarCidadaoAutista/types';
import { AtualizarCidadaoAutistaRequest } from 'store/modules/api/iirgd/cidadaoAutista/atualizarCidadaoAutista/types';
import { enderecosClear } from 'store/modules/api/utils/enderecos/actions';
import { pesquisaNaturalidadeClear } from 'store/modules/sgu/actions';

// COMPONENTS
import CEP, { IBuscarCepResultado } from 'components/Common/Form/Fields/CEP';
import BuscarEndereco, {
	IBuscarEnderecoResultado,
} from 'components/Common/SearchButtons/Endereco';
import Municipio from 'components/Common/Form/Fields/Municipio';
import Input from 'components/Common/Form/Input/Input';
import ButtonImage from 'components/Common/ButtonImage';
import Error from 'components/Common/Notifications/FormError/index';
import Section from 'components/Common/Section';
import Radio from 'components/Common/Form/Radio';
import InputMask from 'components/Common/Form/Input/InputMask';
import CPF from 'components/Common/Form/Fields/CPF';
import data from 'store/modules/api/iirgd/enum';

// FORM
import FormBox from 'components/Common/Form/FormBox';
import { IFormCadastroRg } from '../../form';

export interface CadastroCIPTEAFormValues {
	numeroRg: string;
	digitoRg: string;
	ciptea: boolean;
	dataValidade: string;
	responsavel: {
		cpf: string;
		nome: string;
		logradouro: string;
		logradouroNumero: string;
		logradouroComplemento: string;
		logradouroBairro: string;
		logradouroCEP: string;
		logradouroMunicipioCodigo: string;
		logradouroMunicipioDigito: string;
		celular: string;
		email: string;
	};
	possuiCarteirinha: string;
	codigoMunicipio: string;
	descMunicipio: string;
}

const messagesErrors = {
	CPFNotAllowed:
		'O CPF do responsável encontra-se com a situação irregular junto a RFB, o que impede a continuidade do cadastro.',
	notAllFieldsFilled: 'Dados do Responsável do Autista Incompleto.',
	duplicatedCPF: 'NÚMERO DO CPF DO RESPONSÁVEL É O MESMO CPF DO CIDADÃO.',
	invalidCPF: 'NÚMERO DO CPF DO RESPONSÁVEL É INVALIDO.',
	expiratedDateCards: 'DATA DE VALIDADE DA CARTEIRINHA VENCIDA.',
	invalidExpiratedDate: 'DATA DE VALIDADE INVÁLIDA.',
};

const initialValues: CadastroCIPTEAFormValues = {
	numeroRg: '',
	digitoRg: '',
	ciptea: false,
	dataValidade: '',
	responsavel: {
		cpf: '',
		nome: '',
		logradouro: '',
		logradouroNumero: '',
		logradouroComplemento: '',
		logradouroBairro: '',
		logradouroCEP: '',
		logradouroMunicipioCodigo: '',
		logradouroMunicipioDigito: '',
		email: '',
		celular: '',
	},
	possuiCarteirinha: '',
	codigoMunicipio: '',
	descMunicipio: '',
};

const nameInstanceRequest = 'iirgd-ciptea-form';

let previousCpf = '';

const treatInitialValues = (
	numeroRg: string,
	dataConsultarCidadoAutista: ConsultarCidadaoAutistaData | null,
	descMunicipio?: string,
): CadastroCIPTEAFormValues => {
	const [number, digit] = numeroRg.split('-');

	return {
		possuiCarteirinha: dataConsultarCidadoAutista?.ciptea ? 'S' : 'N',
		numeroRg: number || dataConsultarCidadoAutista?.numeroRg || '',
		digitoRg: digit || dataConsultarCidadoAutista?.digitoRg || '',
		ciptea: dataConsultarCidadoAutista?.ciptea || false,
		dataValidade:
			(dataConsultarCidadoAutista?.ciptea &&
				dataConsultarCidadoAutista.dataValidade &&
				formatDate(dataConsultarCidadoAutista.dataValidade)) ||
			'',
		responsavel: {
			cpf: dataConsultarCidadoAutista?.responsavel?.cpf || '',
			nome: dataConsultarCidadoAutista?.responsavel?.nome || '',
			logradouro: dataConsultarCidadoAutista?.responsavel?.logradouro || '',
			logradouroNumero:
				(dataConsultarCidadoAutista?.responsavel?.logradouroNumero &&
					Number(
						dataConsultarCidadoAutista.responsavel?.logradouroNumero,
					).toString()) ||
				'',
			logradouroComplemento:
				dataConsultarCidadoAutista?.responsavel?.logradouroComplemento || '',
			logradouroBairro:
				dataConsultarCidadoAutista?.responsavel?.logradouroBairro || '',
			logradouroCEP:
				dataConsultarCidadoAutista?.responsavel?.logradouroCEP || '',
			logradouroMunicipioCodigo:
				dataConsultarCidadoAutista?.responsavel?.logradouroMunicipioCodigo ||
				'',
			logradouroMunicipioDigito:
				dataConsultarCidadoAutista?.responsavel?.logradouroMunicipioDigito ||
				'',
			email:
				(dataConsultarCidadoAutista?.responsavel?.emailUsuario &&
					dataConsultarCidadoAutista.responsavel?.emailDominio &&
					`${dataConsultarCidadoAutista.responsavel.emailUsuario}@${dataConsultarCidadoAutista.responsavel.emailDominio}`) ||
				'',
			celular:
				(dataConsultarCidadoAutista?.responsavel?.telefoneDDD &&
					dataConsultarCidadoAutista.responsavel?.telefoneNumero &&
					Number(
						dataConsultarCidadoAutista.responsavel.telefoneDDD,
					).toString() +
						dataConsultarCidadoAutista.responsavel.telefoneNumero) ||
				'',
		},
		codigoMunicipio:
			(dataConsultarCidadoAutista?.responsavel?.logradouroMunicipioCodigo &&
				dataConsultarCidadoAutista.responsavel?.logradouroMunicipioDigito &&
				`${dataConsultarCidadoAutista.responsavel.logradouroMunicipioCodigo}-${dataConsultarCidadoAutista.responsavel.logradouroMunicipioDigito}`) ||
			'',
		descMunicipio: descMunicipio || '',
	};
};

const treatGetRequestCidadaoAutista = (
	login: Login,
	completeRGNumber: string,
): ConsultarCidadaoAutistaRequest => {
	const [number, digit] = completeRGNumber.split('-');
	return {
		header: {
			usuario: login.mainframe.idUsuario,
			senha: login.mainframe.senhaMainFrame,
		},
		query: {
			numeroRg: number,
			digitoRg: digit,
		},
	};
};

const treatPostUpdateRequestData = (
	login: Login,
	formValues: CadastroCIPTEAFormValues,
): CadastrarCidadaoAutistaRequest | AtualizarCidadaoAutistaRequest => {
	const [emailUsuario, emailDominio] = formValues.responsavel.email.split(
		'@',
	) || ['', ''];

	return {
		header: {
			usuario: login.mainframe.idUsuario,
			senha: login.mainframe.senhaMainFrame,
		},
		requestData: {
			numeroRg: formValues.numeroRg,
			digitoRg: formValues.digitoRg,
			ciptea: formValues.possuiCarteirinha === 'S',
			dataValidade:
				(formValues.dataValidade && convertDate(formValues.dataValidade)) ||
				null,
			responsavel: {
				cpf: limparMascara(formValues.responsavel.cpf) || '',
				nome: formValues.responsavel.nome.toUpperCase(),
				logradouro: formValues.responsavel.logradouro,
				logradouroNumero: formValues.responsavel.logradouroNumero,
				logradouroComplemento: formValues.responsavel.logradouroComplemento,
				logradouroBairro: formValues.responsavel.logradouroBairro,
				logradouroCEP: formValues.responsavel.logradouroCEP,
				logradouroMunicipioCodigo:
					formValues.responsavel.logradouroMunicipioCodigo,
				logradouroMunicipioDigito:
					formValues.responsavel.logradouroMunicipioDigito,
				telefoneDDD:
					(formValues.responsavel?.celular &&
						limparMascara(formValues.responsavel.celular).substring(0, 2)) ||
					'',
				telefoneNumero:
					(formValues.responsavel?.celular &&
						limparMascara(formValues.responsavel.celular).substring(2, 11)) ||
					'',
				emailUsuario,
				emailDominio,
			},
		},
	};
};

const schema = Yup.object<CadastroCIPTEAFormValues>().shape({
	possuiCarteirinha: Yup.string().required(
		'Campo Obrigatório: Possui Carteirinha.',
	),

	dataValidade: Yup.string()
		.when(['possuiCarteirinha'], {
			is: (possuiCarteirinha: string) => possuiCarteirinha === 'S',
			then: (sch: any) =>
				sch.required('Campo Obrigatório: Validade da Carteirinha.'),
			otherwise: (sch: any) => sch.notRequired(),
		})
		.test(
			'test-invalid-expirated-date',
			messagesErrors.invalidExpiratedDate,
			(value: any) => {
				if (value && limparMascara(value).length > 0) {
					return validacaoData(value);
				}
				return true;
			},
		)
		.test({
			name: 'validateExpirateDateCard',
			exclusive: true,
			message: messagesErrors.expiratedDateCards,
			test: (value: any) => !verifyDateIsLowerCurrentDate(value),
		}),

	responsavel: Yup.object().when('$dataNascimento', {
		is: () => true,
		then: (sch: any) =>
			sch
				.test(
					'check-all-fields',
					messagesErrors.notAllFieldsFilled,
					function verificarCampos(this: Yup.TestContext, value: any) {
						const context = this.options.context as any;
						const dataNascimento = context?.dataNascimento;
						const isMenor = dataNascimento
							? calcularIdade(dataNascimento) < 16
							: false;

						if (!value || Object.keys(value).length === 0) {
							if (isMenor) {
								return this.createError({
									path: `${this.path}.nome`,
									message: messagesErrors.notAllFieldsFilled,
								});
							}
							return true;
						}

						const campos = { ...value };
						delete campos.logradouroComplemento;

						const valoresPreenchidos = Object.values(campos).filter(
							item => item && String(item).trim() !== '',
						);

						const nenhumCampoPreenchido = valoresPreenchidos.length === 0;
						const todosCamposPreenchidos =
							valoresPreenchidos.length === Object.keys(campos).length;

						if (isMenor) {
							if (!todosCamposPreenchidos) {
								return this.createError({
									path: `${this.path}.nome`,
									message: messagesErrors.notAllFieldsFilled,
								});
							}
							return true;
						}
						if (!nenhumCampoPreenchido && !todosCamposPreenchidos) {
							return this.createError({
								path: `${this.path}.nome`,
								message: messagesErrors.notAllFieldsFilled,
							});
						}
						return true;
					},
				)
				.test(
					'check-duplicate-cpf',
					messagesErrors.duplicatedCPF,
					function validarCPFDuplicado(this: Yup.TestContext, value: any) {
						const context = this.options.context as any;
						const titularCPF = context?.cpf;
						const cpfResponsavel = value?.cpf;

						if (!cpfResponsavel || !titularCPF) return true;

						const isDuplicado =
							limparMascara(cpfResponsavel) === limparMascara(titularCPF);

						if (isDuplicado) {
							return this.createError({
								path: `${this.path}.cpf`,
								message: messagesErrors.duplicatedCPF,
							});
						}

						return true;
					},
				),
		otherwise: (sch: any) => sch.nullable().notRequired(),
	}),
});

interface CipteaFormProps {
	initialData: IFormCadastroRg;
	uploadDivergenceComplete: boolean;
	divergenceScreenEnabled: boolean;
	onCipteaSubmit: (success: boolean) => void;
}

const CipteaForm: React.FC<CipteaFormProps> = ({
	initialData,
	uploadDivergenceComplete,
	divergenceScreenEnabled,
	onCipteaSubmit,
}) => {
	const dispatch = useDispatch();
	const { token } = useContext<IAuthContext>(AuthContext);

	const { notifications } = useSelector((state: ApplicationState) => state.app);
	const { consultarCin } = useSelector(
		(state: ApplicationState) => state.api.iirgd.integrador,
	);
	const { login } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico,
	);
	const {
		consultarCidadaoAutista,
		atualizarCidadaoAutista,
		cadastrarCidadaoAutista,
	} = useSelector((state: ApplicationState) => state.api.iirgd.cidadaoAutista);
	const { consultarNaturalidades } = useSelector(
		(state: ApplicationState) => state.api.sgu.administrar.naturalidades,
	);

	const [avancar, setAvancar] = useState(true);
	const [CPFValidate, setCPFValidate] = useState({
		isInvalid: false,
		errorMessage: '',
	});
	const [formInitialValues, setFormInitialValues] = useState(initialValues);

	useEffect(() => {
		showInside(true);
		if (initialData.numeroRg) {
			const treatedRequest = treatGetRequestCidadaoAutista(
				login,
				initialData.numeroRg,
			);
			dispatch(consultarCidadaoAutistaRequest(treatedRequest));
		}

		return () => {
			dispatch(consultarCidadaoAutistaClear());
			dispatch(cadastrarCidadaoAutistaClear());
			dispatch(atualizarCidadaoAutistaClear());
			dispatch(consultarCpfCinClear());
			dispatch(consultarNaturalidadesClear());
			dispatch(clearNotifications());
			showInside(false);
		};
	}, [dispatch, initialData.numeroRg, login]);

	const handleSubmit = (values: CadastroCIPTEAFormValues) => {
		if (CPFValidate.isInvalid) {
			addNotifications({
				errors: [CPFValidate.errorMessage],
			});
		} else if (
			consultarCidadaoAutista.status === 200 &&
			consultarCidadaoAutista.data?.responsavel?.cpf
		) {
			dispatch(
				atualizarCidadaoAutistaRequest(
					treatPostUpdateRequestData(login, values),
				),
			);
		} else {
			dispatch(
				cadastrarCidadaoAutistaRequest(
					treatPostUpdateRequestData(login, values),
				),
			);
		}
	};

	const handleClearCEPMunicipioData = useCallback(
		(formik: FormikProps<CadastroCIPTEAFormValues>) => {
			const { setFieldValue } = formik;

			dispatch(enderecosClear());
			dispatch(pesquisaNaturalidadeClear());
			setFieldValue('codigoMunicipio', '');
			setFieldValue('responsavel.logradouroMunicipioCodigo', '');
			setFieldValue('responsavel.logradouroMunicipioDigito', '');
			setFieldValue('descMunicipio', '');
		},
		[dispatch],
	);

	const handleCepSelecionado = useCallback(
		(
			res: IBuscarCepResultado,
			formik: FormikProps<CadastroCIPTEAFormValues>,
		) => {
			const { setFieldValue } = formik;

			if (!res.cep) {
				handleClearCEPMunicipioData(formik);
			}

			setFieldValue('responsavel.logradouroCEP', res.cep);
			setFieldValue('responsavel.logradouro', res.endereco.substring(0, 25));
			setFieldValue(
				'responsavel.logradouroBairro',
				res.bairro.substring(0, 14),
			);
			if (res.codigoNaturalidade) {
				const [cod, dig] = patternFormat(
					completaZeroEsquerda(res.codigoNaturalidade, 6),
					'#####-#',
				)?.split('-') || ['', ''];
				setFieldValue('codigoMunicipio', `${cod}-${dig}`);
				setFieldValue('responsavel.logradouroMunicipioCodigo', cod);
				setFieldValue('responsavel.logradouroMunicipioDigito', dig);
				setFieldValue('descMunicipio', res.municipio || '');
			}
		},
		[handleClearCEPMunicipioData],
	);

	const handleEnderecoSelecionado = useCallback(
		(
			res: IBuscarEnderecoResultado,
			formik: FormikProps<CadastroCIPTEAFormValues>,
		) => {
			const { setFieldValue } = formik;
			if (res.cep) setFieldValue('responsavel.logradouroCEP', res.cep);
			const completeAddress = `${res.tipoLogradouro || ''} ${
				res.endereco || ''
			}`.substring(0, 25);
			setFieldValue('responsavel.logradouro', completeAddress);
			setFieldValue(
				'responsavel.logradouroBairro',
				(res.bairro || '').substring(0, 14),
			);
			if (res.codigoMunicipio) {
				const [cod, dig] = patternFormat(
					completaZeroEsquerda(res.codigoMunicipio, 6),
					'#####-#',
				)?.split('-') || ['', ''];
				setFieldValue('codigoMunicipio', `${cod}-${dig}`);
				setFieldValue('responsavel.logradouroMunicipioCodigo', cod);
				setFieldValue('responsavel.logradouroMunicipioDigito', dig);
				setFieldValue('descMunicipio', res.municipio || '');
			}
			setFieldValue('responsavel.logradouroNumero', '');
			setFieldValue('responsavel.logradouroComplemento', '');
		},
		[],
	);

	const throwCPFError = (messageError: string) => {
		addNotifications({ errors: [messageError] });
		setCPFValidate({ isInvalid: true, errorMessage: messageError });
	};

	const clearCPFError = useCallback(() => {
		if (CPFValidate.isInvalid) {
			dispatch(clearNotifications());
			setCPFValidate({ isInvalid: false, errorMessage: '' });
		}
	}, [CPFValidate.isInvalid, dispatch]);

	const handleValidate = useCallback(
		values => {
			return getValidationsErrors(values, schema, undefined, {
				cpf: initialData.cpf,
				dataNascimento: initialData.dataNascimento,
			});
		},
		[initialData],
	);

	function FormikValueWatcher() {
		const { values } = useFormikContext<CadastroCIPTEAFormValues>();
		const treatedCPF = limparMascara(values.responsavel.cpf);

		if (treatedCPF.length === 11 && treatedCPF !== previousCpf) {
			previousCpf = treatedCPF;

			if (!validaCpf(treatedCPF)) {
				throwCPFError(messagesErrors.invalidCPF);
			} else if (
				consultarCin.status === 0 ||
				(consultarCin.cpfRequest && consultarCin.cpfRequest !== treatedCPF)
			) {
				clearCPFError();
				dispatch(
					consultarCpfCinRequest(token, treatedCPF, true, nameInstanceRequest),
				);
			}
		} else if (treatedCPF.length < 11) {
			clearCPFError();
			previousCpf = '';
			if (consultarCin.status !== 0) dispatch(consultarCpfCinClear());
		}

		return null;
	}

	const verifyUploadDivergences = useCallback(() => {
		if (divergenceScreenEnabled && !uploadDivergenceComplete) {
			showInside(false);
			addNotifications({
				errors: [
					'É obrigatório realizar o upload da certidão em caso de divergência com a RFB. Favor selecionar um arquivo válido antes de seguir com o atendimento.',
				],
				moveToTop: true,
			});
		}
	}, [divergenceScreenEnabled, uploadDivergenceComplete]);

	useEffect(() => {
		if (consultarCin?.instanceRequest === nameInstanceRequest) {
			const codNotAllowed = ['2', '3', '5', '8', '9'];
			if (
				(consultarCin.status !== 0 &&
					consultarCin.status !== 100 &&
					consultarCin.status !== 200) ||
				(consultarCin.data?.codSitCad &&
					codNotAllowed.includes(consultarCin.data.codSitCad))
			) {
				throwCPFError(messagesErrors.CPFNotAllowed);
			} else {
				clearCPFError();
			}
		}
	}, [consultarCin, clearCPFError]);

	useEffect(() => {
		if (
			consultarCidadaoAutista.status === 200 &&
			consultarCidadaoAutista.data?.responsavel?.logradouroMunicipioCodigo &&
			!consultarNaturalidades.data
		) {
			dispatch(
				consultarNaturalidadesRequest(
					token,
					{
						id: consultarCidadaoAutista.data.responsavel
							.logradouroMunicipioCodigo,
						digito:
							consultarCidadaoAutista.data.responsavel
								.logradouroMunicipioDigito || '',
					},
					nameInstanceRequest,
				),
			);
		} else {
			setFormInitialValues(
				treatInitialValues(initialData.numeroRg, consultarCidadaoAutista.data),
			);
		}
	}, [
		consultarCidadaoAutista.status,
		consultarCidadaoAutista.data,
		dispatch,
		token,
		consultarNaturalidades.data,
		initialData.numeroRg,
	]);

	useEffect(() => {
		if (
			consultarCidadaoAutista.data &&
			consultarNaturalidades.status === 200 &&
			consultarNaturalidades.data
		) {
			const descMunicipio = consultarNaturalidades.data[0].descricao;
			setFormInitialValues(
				treatInitialValues(
					initialData.numeroRg,
					consultarCidadaoAutista.data,
					descMunicipio,
				),
			);
		}
	}, [
		consultarNaturalidades.status,
		consultarNaturalidades.data,
		consultarCidadaoAutista.data,
		initialData.numeroRg,
	]);

	useEffect(() => {
		if (
			cadastrarCidadaoAutista.status === 201 ||
			atualizarCidadaoAutista.status === 200
		) {
			message.success('Dados CIPTEA salvos com sucesso!');
			onCipteaSubmit(true);
			setAvancar(false);
			verifyUploadDivergences();
		}
	}, [
		cadastrarCidadaoAutista.status,
		atualizarCidadaoAutista.status,
		onCipteaSubmit,
		verifyUploadDivergences,
	]);

	return (
		<Section title="Cadastro CIPTEA">
			<Formik
				validateOnChange={false}
				validateOnBlur={
					notifications.errors.length > 0 && !CPFValidate.isInvalid
				}
				initialValues={formInitialValues}
				enableReinitialize
				onSubmit={handleSubmit}
				validate={handleValidate}
			>
				{formik => {
					const isMenor = initialData.dataNascimento
						? calcularIdade(initialData.dataNascimento) < 16
						: false;

					const camposResponsavel = { ...formik.values.responsavel };
					delete (camposResponsavel as any).logradouroComplemento;

					const algumCampoPreenchido = Object.values(camposResponsavel).some(
						item => item && String(item).trim() !== '',
					);

					const isResponsavelRequired = isMenor || algumCampoPreenchido;

					return (
						<Form autoComplete="off">
							{notifications.errors &&
								notifications.errors.length > 0 &&
								notifications.showInside && (
									<Error errors={notifications.errors} />
								)}
							<Section>
								<Row align="middle" justify="start">
									<Col span={8}>
										<Field
											disabled={!avancar}
											as={Radio}
											required
											title="Possui Carteirinha:"
											name="possuiCarteirinha"
											options={data.simNao}
											value={formik.values.possuiCarteirinha}
											titleSize={110}
											titleAlign="start"
											size={40}
											customChange={(e: RadioChangeEvent) => {
												if (e.target.value === 'N')
													formik.setFieldValue('dataValidade', '');
											}}
										/>
									</Col>
									<Col offset={2} span={12}>
										<Field
											as={InputMask}
											title="Validade da Carteirinha"
											name="dataValidade"
											mask="99/99/9999"
											subtitle="(DD/MM/AAAA)"
											titleAlign="start"
											size={40}
											titleSize={140}
											required={formik.values.possuiCarteirinha === 'S'}
											disabled={
												formik.values.possuiCarteirinha !== 'S' || !avancar
											}
											error={!!formik.errors.dataValidade}
										/>
									</Col>
								</Row>
								<FormBox title="Dados do Responsável do Autista">
									<Row align="middle" justify="start">
										<Col span={24}>
											<Field
												disabled={!avancar}
												as={Input}
												title="Nome"
												titleSize={100}
												titleAlign="start"
												name="responsavel.nome"
												maxLength={60}
												required={isResponsavelRequired}
												value={formik.values.responsavel.nome}
												onChange={(e: ChangeEvent<HTMLInputElement>) => {
													formik.setFieldValue(
														'responsavel.nome',
														nomeIirgdSemNumeros(e.target.value),
													);
												}}
												error={!!formik.errors.responsavel?.nome}
											/>
										</Col>
									</Row>
									<Row align="middle" justify="start">
										<Col span={24}>
											<CPF
												disabled={!avancar}
												required={isResponsavelRequired}
												title="Número CPF"
												titleSize={100}
												titleAlign="start"
												subtitle="Digite Apenas números"
												numero="responsavel.cpf"
												formik={formik}
												error={
													hasError(formik.errors, 'responsavel.cpf') ||
													hasError(
														formik.errors,
														'responsavel.validateDuplicatedCPF',
													) ||
													CPFValidate.isInvalid
												}
											/>
										</Col>
									</Row>
									<Row align="middle" justify="start">
										<Col span={12}>
											<Field
												disabled={!avancar}
												as={InputMask}
												title="Telefone Celular"
												titleSize={100}
												titleAlign="start"
												required={isResponsavelRequired}
												name="responsavel.celular"
												mask="(99)99999-9999"
												error={!!formik.errors?.responsavel?.celular}
											/>
										</Col>
									</Row>
									<Row align="middle" justify="start">
										<Col span={20}>
											<Field
												disabled={!avancar}
												as={Input}
												title="E-mail"
												name="responsavel.email"
												maxLength={60}
												size={200}
												titleSize={100}
												titleAlign="start"
												value={removeAcentoCedilha(
													formik.values.responsavel.email || '',
												)}
												required={isResponsavelRequired}
												error={!!formik.errors?.responsavel?.email}
											/>
										</Col>
									</Row>
									<Row gutter={[0, 10]}>
										<Col span={8}>
											<Row>
												<Col span={16}>
													<CEP
														disabled={!avancar}
														name="responsavel.logradouroCEP"
														titleSize={50}
														titleAlign="start"
														required={isResponsavelRequired}
														formik={formik}
														result={res => handleCepSelecionado(res, formik)}
														isCalledInService="IIRGD"
														instanceRequest={nameInstanceRequest}
														defaultValue={
															formik.values.responsavel.logradouroCEP
														}
													/>
												</Col>
												<Col span={2}>
													<BuscarEndereco
														result={res =>
															handleEnderecoSelecionado(res, formik)
														}
													/>
												</Col>
											</Row>
											<Row>
												<Col span={16}>
													<Field
														disabled={!avancar}
														as={InputMask}
														title="Número"
														titleSize={50}
														titleAlign="start"
														name="responsavel.logradouroNumero"
														mask="99999"
														maskChar=""
														required={isResponsavelRequired}
														error={
															!!formik.errors.responsavel?.logradouroNumero
														}
													/>
												</Col>
											</Row>
											<Row>
												<Col span={24}>
													<Field
														disabled={!avancar}
														as={Input}
														title="Bairro"
														titleSize={50}
														titleAlign="start"
														name="responsavel.logradouroBairro"
														maxLength={14}
														error={
															!!formik.errors.responsavel?.logradouroBairro
														}
														required={isResponsavelRequired}
													/>
												</Col>
											</Row>
										</Col>
										<Col span={15} offset={1}>
											<Row>
												<Col span={20}>
													<Field
														disabled={!avancar}
														as={Input}
														title="Logradouro"
														name="responsavel.logradouro"
														maxLength={25}
														titleSize={100}
														titleAlign="start"
														required={isResponsavelRequired}
														error={!!formik.errors.responsavel?.logradouro}
													/>
												</Col>
											</Row>
											<Row>
												<Col span={20}>
													<Field
														disabled={!avancar}
														as={Input}
														title="Complemento"
														name="responsavel.logradouroComplemento"
														titleSize={100}
														titleAlign="start"
														maxLength={15}
														error={
															!!formik.errors.responsavel?.logradouroComplemento
														}
													/>
												</Col>
											</Row>
											<Row>
												<Col span={20}>
													<Municipio
														codigoMunicipio="codigoMunicipio"
														municipio="descMunicipio"
														sizeInputMunicipio={140}
														sizeInputCodigoMunicipio={70}
														titleSize={100}
														titleAlign="start"
														required={isResponsavelRequired}
														formik={formik}
														codigoValue={formik.values.codigoMunicipio}
														disabled
													/>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row justify="center" style={{ marginTop: '20px' }}>
										<Col>
											<ButtonImage
												type="button"
												src="avancar"
												onClick={() => {
													if (avancar) {
														dispatch(clearNotifications());
														formik.handleSubmit();
													} else {
														verifyUploadDivergences();
													}
												}}
												disabled={!avancar && uploadDivergenceComplete}
											/>
										</Col>
									</Row>
								</FormBox>
							</Section>
							<FormikValueWatcher />
						</Form>
					);
				}}
			</Formik>
		</Section>
	);
};

export default CipteaForm;
